<template>
  <aside class="sidebar" :class="{ embedded }">
    <div v-if="!embedded" class="sidebar-head">
      <span>Containers</span>
      <LgIconButton label="Refresh container list" size="sm" @click="$emit('refresh')">
        <RotateCw :size="15" aria-hidden="true" />
      </LgIconButton>
    </div>

    <div v-if="loading && !containers.length" class="sidebar-note">Loading…</div>
    <div v-else-if="error" class="sidebar-note err">
      Couldn't load containers
      <button class="link-btn" @click="$emit('refresh')">retry</button>
    </div>
    <div v-else-if="!containers.length" class="sidebar-note">No containers seen yet.</div>

    <div v-else class="groups">
      <section
        v-for="group in groups"
        :key="group.stack ?? '∅'"
        :ref="(el) => setRowRef(group.stack, el)"
        class="group"
        :class="{ dragging: draggingStack === group.stack }"
      >
        <div class="group-head-row">
          <button
            v-if="group.stack"
            type="button"
            class="grip"
            :aria-label="`Reorder stack ${group.stack}. Use arrow up or down to move.`"
            @pointerdown="onGripPointerDown($event, group.stack)"
            @keydown="onGripKeydown($event, group.stack)"
          >
            <GripVertical :size="14" aria-hidden="true" />
          </button>
          <span v-else class="grip-spacer" aria-hidden="true"></span>

          <button
            class="group-head"
            :class="{ active: isStackActive(group.stack) }"
            :title="
              group.stack
                ? `Filter by stack: ${group.stack} (⌘/Ctrl-click to add to selection)`
                : 'Ungrouped containers'
            "
            :disabled="!group.stack"
            @click="group.stack && onStackClick($event, group.stack)"
          >
            <span class="dot" :class="{ on: group.anyRunning }"></span>
            <span class="group-name">{{ group.stack ?? 'ungrouped' }}</span>
            <span class="count">{{ group.total }}</span>
          </button>
        </div>

        <button
          v-for="c in group.items"
          :key="c.container"
          class="item"
          :class="{ active: isContainerActive(c.container) }"
          :title="`Filter by container: ${c.container} (⌘/Ctrl-click to add to selection)`"
          @click="onContainerClick($event, c.container)"
        >
          <span class="dot" :class="{ on: c.running }"></span>
          <span class="item-name" :style="{ color: containerColor(c.container) }">{{
            c.container
          }}</span>
          <span class="count">{{ c.count24h }}</span>
        </button>
      </section>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed, ref, onBeforeUnmount } from 'vue';
import { RotateCw, GripVertical } from 'lucide-vue-next';
import type { ContainerInfo } from '../api.js';
import { containerColor } from '../format.js';
import { mergeSidebarOrder } from '../composables/useUserSettings.js';
import LgIconButton from '../ui/LgIconButton.vue';

const props = defineProps<{
  containers: ContainerInfo[];
  activeStacks: string[];
  activeContainers: string[];
  loading: boolean;
  error: boolean;
  /** Persisted (or optimistically-updated) stack order from `useUserSettings`. */
  stackOrder: string[];
  /** When true (mobile drawer), drop the fixed width / border chrome. */
  embedded?: boolean;
}>();

const emit = defineEmits<{
  /** Sidebar click semantics: `additive` true = old toggle behavior (⌘/Ctrl-click). */
  'toggle-stack': [stack: string, additive: boolean];
  'toggle-container': [container: string, additive: boolean];
  refresh: [];
  /** Full new stack-name order, ready to persist via `sidebarOrder`. */
  'reorder-stacks': [order: string[]];
}>();

interface Group {
  stack: string | null;
  items: ContainerInfo[];
  total: number;
  anyRunning: boolean;
}

/** Effective stack order: persisted prefs merged with whatever stacks currently exist. */
const stackNames = computed(() => {
  const known = [...new Set(props.containers.map((c) => c.stack).filter((s): s is string => !!s))];
  return mergeSidebarOrder(props.stackOrder, known);
});

// While actively dragging, render the live in-progress order instead of the
// merged prop order (which won't reflect the drag until the parent persists
// it and the prop comes back around).
const draggingStack = ref<string | null>(null);
const workingOrder = ref<string[] | null>(null);
const displayOrder = computed(() => workingOrder.value ?? stackNames.value);

const groups = computed<Group[]>(() => {
  const map = new Map<string, Group>();
  for (const c of props.containers) {
    const key = c.stack ?? '';
    let g = map.get(key);
    if (!g) {
      g = { stack: c.stack, items: [], total: 0, anyRunning: false };
      map.set(key, g);
    }
    g.items.push(c);
    g.total += c.count24h;
    if (c.running) g.anyRunning = true;
  }
  const list = [...map.values()];
  for (const g of list) g.items.sort((a, b) => a.container.localeCompare(b.container));

  const named = list.filter((g) => g.stack !== null);
  const ungrouped = list.filter((g) => g.stack === null);
  const order = displayOrder.value;
  named.sort((a, b) => order.indexOf(a.stack as string) - order.indexOf(b.stack as string));

  // Ungrouped containers stay a fixed group at the end — only stacks reorder.
  return [...named, ...ungrouped];
});

function isStackActive(stack: string | null): boolean {
  return stack !== null && props.activeStacks.includes(stack);
}
function isContainerActive(container: string): boolean {
  return props.activeContainers.includes(container);
}

function onStackClick(e: MouseEvent, stack: string) {
  emit('toggle-stack', stack, e.metaKey || e.ctrlKey);
}
function onContainerClick(e: MouseEvent, container: string) {
  emit('toggle-container', container, e.metaKey || e.ctrlKey);
}

// --- Drag reorder (pointer events, no library) -----------------------------
// Only stack rows are draggable; containers stay grouped under their stack.
const rowEls = new Map<string, HTMLElement>();
function setRowRef(stack: string | null, el: unknown) {
  if (!stack) return;
  if (el) rowEls.set(stack, el as HTMLElement);
  else rowEls.delete(stack);
}

function onGripPointerDown(e: PointerEvent, stack: string) {
  e.preventDefault();
  draggingStack.value = stack;
  workingOrder.value = [...stackNames.value];
  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp, { once: true });
  // The browser/OS can interrupt a pointer gesture (e.g. a system gesture takes over, or the
  // element loses capture) without ever firing pointerup -- without this, the drag listeners and
  // dragging state would be stuck on indefinitely.
  window.addEventListener('pointercancel', onPointerCancel, { once: true });
}

function onPointerMove(e: PointerEvent) {
  const dragged = draggingStack.value;
  const order = workingOrder.value;
  if (!dragged || !order) return;
  const draggedIdx = order.indexOf(dragged);
  if (draggedIdx < 0) return;
  for (let i = 0; i < order.length; i++) {
    if (i === draggedIdx) continue;
    const el = rowEls.get(order[i]);
    if (!el) continue;
    const rect = el.getBoundingClientRect();
    const mid = rect.top + rect.height / 2;
    const crossedUp = i < draggedIdx && e.clientY < mid;
    const crossedDown = i > draggedIdx && e.clientY > mid;
    if (crossedUp || crossedDown) {
      const next = [...order];
      const [item] = next.splice(draggedIdx, 1);
      next.splice(i, 0, item);
      workingOrder.value = next;
      break;
    }
  }
}

/** Removes the window-level drag listeners and clears drag state; shared by a normal drop
 * (pointerup), a cancelled gesture (pointercancel), and unmount. */
function endDrag() {
  window.removeEventListener('pointermove', onPointerMove);
  window.removeEventListener('pointerup', onPointerUp);
  window.removeEventListener('pointercancel', onPointerCancel);
  draggingStack.value = null;
  workingOrder.value = null;
}

function onPointerUp() {
  if (draggingStack.value && workingOrder.value) {
    emit('reorder-stacks', workingOrder.value);
  }
  endDrag();
}

/** Cancelled gesture (e.g. the OS/browser interrupted it) -- discard the in-progress reorder
 * rather than committing a possibly-incomplete drag. */
function onPointerCancel() {
  endDrag();
}

// If the component unmounts mid-drag (mobile drawer closes, desktop sidebar torn down, a
// logout/login remount, etc.), these window-level listeners would otherwise leak and keep firing
// against a detached component instance.
onBeforeUnmount(() => {
  endDrag();
});

function onGripKeydown(e: KeyboardEvent, stack: string) {
  if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
  e.preventDefault();
  const delta = e.key === 'ArrowUp' ? -1 : 1;
  const order = [...stackNames.value];
  const idx = order.indexOf(stack);
  const next = idx + delta;
  if (idx < 0 || next < 0 || next >= order.length) return;
  [order[idx], order[next]] = [order[next], order[idx]];
  emit('reorder-stacks', order);
}
</script>

<style scoped>
.sidebar {
  width: var(--lg-sidebar-w);
  flex-shrink: 0;
  border-right: 1px solid var(--lg-border);
  background: var(--lg-bg-raised);
  overflow-y: auto;
  font-size: var(--lg-fs-md);
}
.sidebar.embedded {
  width: 100%;
  border-right: none;
  background: transparent;
}
.sidebar-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--lg-space-2) var(--lg-space-3);
  color: var(--lg-text-dim);
  text-transform: uppercase;
  font-size: var(--lg-fs-xs);
  letter-spacing: 0.05em;
  position: sticky;
  top: 0;
  background: var(--lg-bg-raised);
  border-bottom: 1px solid var(--lg-border-faint);
}

.sidebar-note {
  padding: var(--lg-space-3);
  color: var(--lg-text-dim);
}
.sidebar-note.err {
  color: var(--lg-level-error);
}
.link-btn {
  background: none;
  border: none;
  color: var(--lg-accent);
  cursor: pointer;
  text-decoration: underline;
  font: inherit;
}

.group {
  padding: var(--lg-space-1) 0;
  border-bottom: 1px solid var(--lg-border-faint);
}
.group.dragging {
  background: var(--lg-bg-hover);
}
.group-head-row {
  display: flex;
  align-items: stretch;
}
.grip,
.grip-spacer {
  flex-shrink: 0;
  width: 20px;
}
.grip {
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--lg-text-faint);
  cursor: grab;
  touch-action: none;
  padding: 0;
}
.grip:hover,
.grip:focus-visible {
  color: var(--lg-text);
}
.grip:focus-visible {
  outline: none;
  box-shadow: var(--lg-focus);
}
.group-head,
.item {
  display: grid;
  grid-template-columns: 12px 1fr auto;
  gap: var(--lg-space-2);
  align-items: center;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  cursor: pointer;
  font: inherit;
  padding: var(--lg-space-1) var(--lg-space-3);
  color: var(--lg-text);
}
.group-head {
  flex: 1;
  min-width: 0;
}
.group-head:disabled {
  cursor: default;
}
.group-head {
  font-weight: 600;
}
.group-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.item {
  padding-left: var(--lg-space-5);
}
.item-name {
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.group-head:hover:not(:disabled),
.item:hover {
  background: var(--lg-bg-hover);
}
.group-head.active,
.item.active {
  background: var(--lg-bg-active);
  box-shadow: inset 3px 0 0 var(--lg-accent);
}
.group-head:focus-visible,
.item:focus-visible {
  outline: none;
  box-shadow: var(--lg-focus);
}
.count {
  color: var(--lg-text-faint);
  font-variant-numeric: tabular-nums;
  font-size: var(--lg-fs-xs);
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: var(--lg-radius-pill);
  background: var(--lg-dot-off);
}
.dot.on {
  background: var(--lg-dot-on);
}

/* Comfortable touch targets on mobile / in the drawer */
@media (max-width: 768px) {
  .group-head,
  .item {
    min-height: var(--lg-touch);
  }
  .grip,
  .grip-spacer {
    width: var(--lg-touch);
  }
}
.sidebar.embedded .group-head,
.sidebar.embedded .item {
  min-height: var(--lg-touch);
}
.sidebar.embedded .grip,
.sidebar.embedded .grip-spacer {
  width: var(--lg-touch);
}
</style>
